import { loginController } from '@services/login-controller';
import { Component, Host, Prop, State, Watch, h } from '@stencil/core';
import { If } from '@utils/If';
import { handleRedirectWithDisclaimerLogic } from '@utils/utilities';
import { ConfigService } from '../../../services/config-service';
import { Disclaimer, PartnerDisclaimerData } from '../types';
import { NavFooterItem } from './types';

@Component({
  tag: 'nav-footer',
  styleUrl: 'nav-footer.scss',
  shadow: true
})

export class NavFooter {
  @Prop() assetPath: string;
  @Prop() isLoggedIn: boolean = false;
  @Prop() redirectDomainUrl = '';
  @Prop() isVisible: boolean = true;
  @Prop() navItems: NavFooterItem[];
  @Prop() disclaimerData: PartnerDisclaimerData;
  @Prop() ssrEnabled: boolean = false;
  @Prop() isMain: boolean = false;
  @Prop() pathsAvailable: string[] = [];
  @State() disclaimerContent: Disclaimer
  @State() activeSection: string = 'home';
  @State() showRedirectPopup: boolean = false;
  @State() redirectPopupContentType: string;
  @State() redirectTargetUrl: string = '';
  @State() showDiscoverModal: boolean = false;
  @State() navigatingKey: string | null = null;

  @Watch('navItems')
  handleNavItems() {
    if (!this.navItems) return;
    if (typeof this.navItems === 'string') {
      try {
        this.navItems = JSON.parse(atob(this.navItems));
      } catch (e) {
        console.error('Invalid navItems format');
        return;
      }
    }

    if (typeof this.pathsAvailable === 'string') {
      try {
        this.pathsAvailable = JSON.parse(atob(this.pathsAvailable));
      } catch (e) {
        console.error('Invalid pathsAvailable format');
      }
    }

    const currentUrl = window.location.href;
    const normalize = (str: string) => str.replace(/\/+$/, ''); // remove trailing slashes

    const currentPath = normalize(new URL(currentUrl).pathname); // → "/v3/infinia/compare-fly"
    let pathMatched = false;
    for (const item of this.navItems) {
      if (!item.link) continue;

      let itemPath = '';

      try {
        itemPath = normalize(new URL(item.link).pathname);
      } catch (e) {
        // fallback if item.link is not a full URL
        itemPath = normalize(item.link);
      }

      // Exact match
      if (currentPath === itemPath) {
        this.activeSection = item.key;
        pathMatched = true;
        break;
      }
      const lastPath = itemPath.replace(/^\/+|\/+$/g, '').split('/').pop() || '';
      // Strategy 1: Suffix match (currentPath ends with itemPath)
      if (currentPath.endsWith(itemPath)) {
        this.activeSection = item.key;
        pathMatched = true;
        break;
      }
      // Strategy 2: Last segment match (lastPath is in pathsAvailable)
      if (this.pathsAvailable.includes(lastPath)) {
        this.activeSection = item.key;
        pathMatched = true;
        break;
      }
    }

    if (!pathMatched) {
      this.activeSection = '';
      const event = new CustomEvent('no-path-matched');
      window.dispatchEvent(event);
    }
  }

  private spawnRipple = (ev: MouseEvent) => {
    const target = (ev.currentTarget as HTMLElement) ?? (ev.target as HTMLElement);
    if (!target) return;
    const host = target.closest('.footer-item') as HTMLElement;
    if (!host) return;

    const rect = host.getBoundingClientRect();
    host.style.setProperty('--rp-x', `${ev.clientX - rect.left}px`);
    host.style.setProperty('--rp-y', `${ev.clientY - rect.top}px`);
    host.classList.remove('clicked'); // restart animation if double-tapped quickly
    // next frame to reflow
    requestAnimationFrame(() => host.classList.add('clicked'));
    // optional haptic
    if (navigator?.vibrate) navigator.vibrate(12);
    // cleanup after animation
    setTimeout(() => host.classList.remove('clicked'), 500);
  };

  @Watch('activeSection')
  resetNavigatingOnActiveChange() {
    // if route/tab changed (SPA), clear the "Redirecting..." label
    this.navigatingKey = null;
  }
  openDiscoverModal() {
    this.showDiscoverModal = true;
  }

  closeDiscoverModal() {
    if (this.showDiscoverModal) {
      this.showDiscoverModal = false;
      this.handleNavItems();
    }
  }

  handleRedirectClose = () => {
    this.showRedirectPopup = false;
  };

  handleRedirectProceed = () => {
    if (this.redirectTargetUrl) {
      window.location.href = this.redirectTargetUrl;
    }
  };


  viewSection = (event: MouseEvent, item: NavFooterItem) => {
    const { key, link, disclaimer_popup } = item;
    this.activeSection = key;
    this.navigatingKey = key;
    event.preventDefault();
    this.spawnRipple(event);
    // Always close modal first
    this.closeDiscoverModal();

    if (key === 'profile' && !this.isLoggedIn) {
      if (this.isMain && ConfigService.sessionSharingDomain) {
        window.location.href = ConfigService.sessionCheckLoginPath;
        return;
      } else {
        loginController.requestLogin(
          () => { },
          {
            notifyParent: true,
            eventName: 'show-smartbuy-login',
            isLoginRequired: false
          }
        );
      }
      return;
    }

    if (key === 'discover') {
      this.activeSection = key;
      this.openDiscoverModal();
      return;
    }

    const result = handleRedirectWithDisclaimerLogic({
      redirectUrl: link,
      disclaimerSlug: disclaimer_popup?.slug,
    });

    if (result.showPopup) {
      this.redirectPopupContentType = result.contentType;
      this.redirectTargetUrl = result.redirectUrl;
      this.disclaimerContent = result.disclaimerContent;
      this.showRedirectPopup = true;
    }
  };

  componentWillLoad() {
    if (typeof this.navItems === 'string') {
      const decoded = atob(this.navItems); // Base64 decode
      this.navItems = JSON.parse(decoded);
    }
    // const parsedDisclaimerData = typeof this.disclaimerData === 'string' ? JSON.parse(atob(this.disclaimerData)) : this.disclaimerData;
    // if (parsedDisclaimerData) {
    //   disclaimerStore.disclaimerData = parsedDisclaimerData;
    // }
    // pathStore.assetPath = this.assetPath;

    this.handleNavItems();
  }

  render() {
    if (!this.isVisible) {
      return null;
    }
    return (
      <Host>
        <div class={`fixed md:hidden bottom-0 left-0 right-0 z-[20] flex items-center justify-between bg-white rounded-t-xl footer ${this.ssrEnabled ? '' : 'nav-footer-animate'}`}>
          {this.navItems?.map((item: NavFooterItem, idx: number) => {
            return (
              <div
                onClick={(event: MouseEvent) => this.viewSection(event, item)}
                key={'footer_item' + idx}
                class={`footer-item flex cursor-pointer flex-1 grow-1 pt-2 pb-1 flex-col gap-1 items-center leading-4 border-b-3 border-solid relative min-w-14 ${this.activeSection === item.key ? 'border-nav-footer-highlight' : 'border-transparent'} `}
              >
                <div class="relative">
                  <img alt="" class={this.activeSection !== item.key && 'hidden'} src={item.activeIcon} />
                  <img alt="" class={this.activeSection === item.key && 'hidden'} src={item.inactiveIcon} />
                  <If
                    condition={!!item.badge && !!item.badge.title?.trim()}
                    children={
                      <div class="btmbar_badge absolute leading-normal break-words line-clamp-2 overflow-hidden "> {item.badge?.title}</div>
                    }
                  />
                </div>
                <span class={`text-[10px] text-nowrap ${this.activeSection === item.key ? 'font-bold text-nav-footer-highlight' : 'font-medium text-gray-500'}`}>{item.title}</span>
              </div>
            );
          })}
        </div>
        {/* Dicover Modal */}
        <If
          condition={this.showDiscoverModal}
          children={<div class="discover_modal fixed inset-0 bg-black bg-opacity-50 z-30 flex items-center justify-center">
            <div class="bg-white shadow-xl w-screen h-full overflow-y-scroll max-w-lg relative slide_Up">
              <discover-page onCloseDiscover={() => this.closeDiscoverModal()} />
            </div>
          </div>}
        />
        <If
          condition={this.showRedirectPopup}
          children={<redirect-popup
            open={this.showRedirectPopup}
            contentType={this.redirectPopupContentType}
            onConfirm={this.handleRedirectProceed}
            onClose={this.handleRedirectClose}
            redirectTargetUrl={this.redirectTargetUrl}
            disclaimerContent={this.disclaimerContent}
          />}
        />
      </Host>
    );
  }
}
